import axios from 'axios'
import { getCustomerId } from 'utils/auth'
import { loadAuthToken } from './auth'
import { JUST_SESSION_EXPIRED_KEY } from 'constants/sessionStorage'
import { AUTH_ERROR_TYPES } from 'constants/errorCodes'
import unleashClient from 'services/unleashClient'

/**
 * @param {string} url - Base URL for the API
 * @param {boolean} [rawResponse=false] - Return raw axios response instead of response.data
 * @param {object} [options] - Optional configuration
 * @param {boolean} [options.skipAuthRedirect=false] - When true, a 401 propagates as a plain rejection
 *   instead of triggering the auth error handler (redirect to login) — use for logout where a redirect would block the flow
 * @returns {import('axios').AxiosInstance} Configured axios instance
 */
function httpClient(url, rawResponse = false, { skipAuthRedirect = false } = {}) {
  const token = loadAuthToken()
  const customerId = getCustomerId()
  const customerIdHeader = customerId ? { 'X-Irwin-Customer-Id': customerId } : {}
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {}

  // TODO: remove X-Irwin-New-Auth when Auth0 is fully removed
  const newAuthHeader = { 'X-Irwin-New-Auth': 'true' }

  const featureFlags = unleashClient()
    .getAllToggles()
    .filter(toggle => toggle.enabled)
    .map(toggle => toggle.name)
  const featureFlagsHeader = { 'X-Irwin-Feature-Flags': featureFlags.join(',') }

  const API = axios.create({
    baseURL: url,
    responseType: 'json',
    headers: { ...authHeader, ...customerIdHeader, ...newAuthHeader, ...featureFlagsHeader },
  })

  API.interceptors.response.use(
    response => (rawResponse ? response : response.data),
    skipAuthRedirect
      ? undefined
      : error => {
          if (error?.response?.status === 401) {
            const errors = error.response.data?.errors || []
            if (errors.some(e => e.code === AUTH_ERROR_TYPES.identityLocked)) {
              return Promise.reject(error)
            }

            // Redirect to the session-expired interstitial.
            sessionStorage.setItem(JUST_SESSION_EXPIRED_KEY, 'true')
            window.location.href = window.location.origin
            // Never resolves — keeps callers suspended while the browser navigates away,
            // preventing a null-response crash that would trigger an error boundary.
            return new Promise(() => {})
          }

          return Promise.reject(error)
        },
  )

  return API
}

export { httpClient }
